import React, { useState, useEffect, useRef } from 'react';
import { Toast } from 'primereact/toast';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Avatar } from 'primereact/avatar';
import { Checkbox } from 'primereact/checkbox';
import { GlobalDropdownField, DropdownOption } from '../../components/GlobalComponents/GlobalDropdownField';
import { GlobalButton } from '../../components/GlobalComponents/GlobalButton';
import { useOperatorProfileStore } from '../../stateManagers/stores/operatorProfileStore';
import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';

interface Teacher {
  teacher_id: string;
  name: string;
  email: string;
  mobileNumber: string;
  employeeId: string;
  qualification: string;
  status: string;
  profilePicture?: { cdn_url?: string; dev_url?: string; };
}

interface TeacherPermissions {
  noticeboard: {
    enabled: boolean;
    actions: { create: boolean; read: boolean; update: boolean; delete: boolean; };
  };
  students: {
    enabled: boolean;
    actions: { create: boolean; read: boolean; update: boolean; delete: boolean; };
  };
  fee_management: {
    enabled: boolean;
    actions: { create: boolean; read: boolean; update: boolean; delete: boolean; };
  };
}

interface TeacherPermissionSettings {
  setting_id?: string;
  teacher_id: string;
  permissions: TeacherPermissions;
}

const TeacherPermissionsSettings: React.FC = () => {
  const toast = useRef<Toast>(null);
  const { profile, loading: profileLoading } = useOperatorProfileStore();

  const [teachers, setTeachers] = useState<Teacher[]>([]);
  const [teachersLoading, setTeachersLoading] = useState(false);
  const [selectedTeacherId, setSelectedTeacherId] = useState<string | null>(null);
  const [selectedTeacher, setSelectedTeacher] = useState<Teacher | null>(null);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [permissions, setPermissions] = useState<TeacherPermissions>({
    noticeboard: { enabled: false, actions: { create: false, read: false, update: false, delete: false } },
    students: { enabled: false, actions: { create: false, read: false, update: false, delete: false } },
    fee_management: { enabled: false, actions: { create: false, read: false, update: false, delete: false } }
  });

  // ── Toast helpers ─────────────────────────────────────────────────────────
  const showSuccess = (detail: string, summary = 'Success') =>
    toast.current?.show({ severity: 'success', summary, detail, life: 3200 });
  const showError = (detail: string, summary = 'Error') =>
    toast.current?.show({ severity: 'error', summary, detail, life: 4500 });
  const showInfo = (detail: string, summary = 'Info') =>
    toast.current?.show({ severity: 'info', summary, detail, life: 3000 });

  // ── Fetch teachers ────────────────────────────────────────────────────────
  useEffect(() => {
    if (profile?.institute_id && profile?.branch_id) fetchTeachers();
  }, [profile]);

  const fetchTeachers = async () => {
    try {
      setTeachersLoading(true);
      setError(null);
      const response = await apiClient.get('/v1/teachers/get-teachers', {
        params: { page: 1, limit: 100, status: 'active' }
      });
      if (response.data.success) {
        setTeachers(response.data.data);
      } else { showError('Failed to load teachers'); }
    } catch (err: any) {
      showError(err.response?.data?.error || 'Failed to load teachers');
    } finally { setTeachersLoading(false); }
  };

  // ── Fetch permissions when teacher selected ───────────────────────────────
  useEffect(() => {
    if (selectedTeacherId) {
      fetchTeacherPermissions(selectedTeacherId);
      setSelectedTeacher(teachers.find(t => t.teacher_id === selectedTeacherId) || null);
    } else {
      setSelectedTeacher(null);
      resetPermissions();
    }
  }, [selectedTeacherId, teachers]);

  const fetchTeacherPermissions = async (teacherId: string) => {
    try {
      setPermissionsLoading(true);
      setError(null);
      const response = await apiClient.get(`/v1/settings/teacher-permissions/${teacherId}`);
      if (response.data.success && response.data.data) {
        setPermissions(response.data.data.permissions || getDefaultPermissions());
      } else { resetPermissions(); }
    } catch (err: any) {
      if (err.response?.status !== 404) showError(err.response?.data?.error || 'Failed to load permissions');
      resetPermissions();
    } finally { setPermissionsLoading(false); }
  };

  const getDefaultPermissions = (): TeacherPermissions => ({
    noticeboard: { enabled: false, actions: { create: false, read: false, update: false, delete: false } },
    students: { enabled: false, actions: { create: false, read: false, update: false, delete: false } },
    fee_management: { enabled: false, actions: { create: false, read: false, update: false, delete: false } }
  });

  const resetPermissions = () => setPermissions(getDefaultPermissions());

  const handleModuleToggle = (module: keyof TeacherPermissions) => {
    setPermissions(prev => ({
      ...prev,
      [module]: {
        ...prev[module],
        enabled: !prev[module].enabled,
        actions: !prev[module].enabled
          ? prev[module].actions
          : { create: false, read: false, update: false, delete: false }
      }
    }));
  };

  const handleActionToggle = (
    module: keyof TeacherPermissions,
    action: 'create' | 'read' | 'update' | 'delete'
  ) => {
    setPermissions(prev => ({
      ...prev,
      [module]: {
        ...prev[module],
        actions: { ...prev[module].actions, [action]: !prev[module].actions[action] }
      }
    }));
  };

  const handleSavePermissions = async () => {
    if (!selectedTeacherId) { showError('Please select a teacher first'); return; }
    if (!profile?.institute_id || !profile?.branch_id) { showError('Institute or Branch information is missing'); return; }
    try {
      setSaving(true);
      setError(null);
      const response = await apiClient.post('/v1/settings/teacher-permissions', {
        institute_id: profile.institute_id,
        branch_id: profile.branch_id,
        teacher_id: selectedTeacherId,
        permissions
      });
      if (response.data.success) {
        showSuccess('Teacher permissions saved successfully!', 'Permissions Updated');
      } else { showError(response.data.message || 'Failed to save permissions'); }
    } catch (err: any) {
      showError(err.response?.data?.message || err.response?.data?.error || 'Failed to save permissions');
    } finally { setSaving(false); }
  };

  const handleResetPermissions = () => {
    if (selectedTeacherId) { resetPermissions(); showInfo('Permissions reset to default (all disabled)'); }
  };

  const teacherOptions: DropdownOption[] = teachers.map(t => ({
    label: `${t.name} - ${t.employeeId || 'No ID'}`,
    value: t.teacher_id
  }));

  const modules = [
    { key: 'noticeboard' as keyof TeacherPermissions, label: 'Noticeboard', icon: 'pi-megaphone', description: 'Manage notices and announcements', accentColor: '#0e225d' },
    { key: 'students' as keyof TeacherPermissions, label: 'Students', icon: 'pi-users', description: 'View and manage student information', accentColor: '#15803d' },
    { key: 'fee_management' as keyof TeacherPermissions, label: 'Fee Management', icon: 'pi-wallet', description: 'Handle fee collection and records', accentColor: '#b8860b' }
  ];

  // ── Card style shorthand ──────────────────────────────────────────────────
  const cardStyle: React.CSSProperties = {
    background: 'white', borderRadius: '16px', padding: '24px',
    marginBottom: '20px', boxShadow: '0 2px 8px rgba(14,34,93,0.06)',
    border: '1px solid #E5E7EB'
  };

  const cardHeaderStyle: React.CSSProperties = {
    display: 'flex', alignItems: 'center', gap: '10px',
    marginBottom: '20px', paddingBottom: '14px',
    borderBottom: '2px solid #E5E7EB'
  };

  // ── Guards ────────────────────────────────────────────────────────────────
  if (profileLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px', flexDirection: 'column', gap: '16px' }}>
        <ProgressSpinner style={{ width: '48px', height: '48px' }} strokeWidth="3" animationDuration=".8s" />
        <p style={{ color: '#0e225d', fontSize: '0.95rem', fontWeight: '500' }}>Loading...</p>
      </div>
    );
  }

  if (!profile) {
    return (
      <div style={{ padding: '24px' }}>
        <Message severity="warn" text="Unable to load profile information. Please refresh the page." style={{ fontSize: '0.9rem' }} />
      </div>
    );
  }

  return (
    <div style={{ padding: '0', margin: '0 auto' }}>
      <Toast ref={toast} position="top-right" />

      {/* ── Hero Header ───────────────────────────────────────────────────── */}
      <div style={{
        background: 'linear-gradient(180deg, #0e225d 0%, #1a3370 50%, #0a1a3d 100%)',
        borderRadius: '20px', padding: '32px', marginBottom: '24px',
        color: 'white', position: 'relative', overflow: 'hidden',
        boxShadow: '2px 0 12px rgba(14,34,93,0.3)'
      }}>
        <div style={{ position: 'absolute', top: '-60px', right: '-60px', width: '220px', height: '220px', background: 'rgba(251,178,31,0.08)', borderRadius: '50%', filter: 'blur(50px)', pointerEvents: 'none' }} />
        <div style={{ position: 'absolute', bottom: '-40px', left: '30%', width: '140px', height: '140px', background: 'rgba(251,178,31,0.05)', borderRadius: '50%', filter: 'blur(40px)', pointerEvents: 'none' }} />

        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px',
            padding: '4px 12px', background: 'rgba(251,178,31,0.18)',
            border: '1px solid rgba(251,178,31,0.35)', borderRadius: '20px',
            marginBottom: '12px', fontSize: '0.68rem', fontWeight: '700',
            letterSpacing: '0.8px', textTransform: 'uppercase', color: '#fbb21f'
          }}>
            <i className="pi pi-cog" style={{ fontSize: '0.7rem' }}></i>
            Settings
          </div>
          <h1 style={{ margin: '0 0 8px 0', fontSize: '1.75rem', fontWeight: '700', color: 'white', letterSpacing: '-0.5px' }}>
            Teacher Permissions
          </h1>
          <p style={{ margin: 0, color: 'rgba(255,255,255,0.6)', fontSize: '0.88rem' }}>
            Manage access permissions for teachers
          </p>
        </div>
      </div>

      {/* ── Error ─────────────────────────────────────────────────────────── */}
      {error && <Message severity="error" text={error} style={{ marginBottom: '20px', fontSize: '0.9rem' }} />}

      {/* ── Teacher Selection Card ────────────────────────────────────────── */}
      <div style={cardStyle}>
        <div style={cardHeaderStyle}>
          <div style={{ width: '4px', height: '22px', background: 'linear-gradient(180deg, #fbb21f 0%, #e09a00 100%)', borderRadius: '4px' }} />
          <div style={{
            width: '38px', height: '38px',
            background: 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)',
            borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 2px 8px rgba(14,34,93,0.2)'
          }}>
            <i className="pi pi-user" style={{ color: '#fbb21f', fontSize: '1rem' }}></i>
          </div>
          <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: '#0e225d' }}>Select Teacher</h2>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '16px', alignItems: 'end' }}>
          <GlobalDropdownField
            label="Teacher"
            value={selectedTeacherId}
            onChange={(val) => setSelectedTeacherId(val)}
            options={teacherOptions}
            placeholder="Select a teacher to manage permissions"
            icon="pi pi-user"
            filter
          />
          {selectedTeacherId && (
            <button
              onClick={() => fetchTeacherPermissions(selectedTeacherId)}
              disabled={permissionsLoading}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                padding: '0 18px', height: '44px',
                background: '#F5F7FA', border: '1px solid #E5E7EB',
                borderRadius: '10px', color: '#0e225d',
                fontSize: '0.82rem', fontWeight: '700', cursor: permissionsLoading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease', opacity: permissionsLoading ? 0.6 : 1, whiteSpace: 'nowrap'
              }}
              onMouseEnter={(e) => { if (!permissionsLoading) { e.currentTarget.style.background = '#E8EEF7'; e.currentTarget.style.borderColor = '#fbb21f'; } }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#F5F7FA'; e.currentTarget.style.borderColor = '#E5E7EB'; }}
            >
              <i className={`pi ${permissionsLoading ? 'pi-spin pi-spinner' : 'pi-refresh'}`} style={{ fontSize: '0.82rem' }}></i>
              REFRESH
            </button>
          )}
        </div>

        {/* Selected teacher info strip */}
        {selectedTeacher && (
          <div style={{
            marginTop: '20px', padding: '16px 20px',
            background: 'rgba(14,34,93,0.03)', borderRadius: '12px',
            border: '1px solid #E5E7EB',
            display: 'flex', alignItems: 'center', gap: '16px'
          }}>
            <Avatar
              image={selectedTeacher.profilePicture?.cdn_url || undefined}
              icon={!selectedTeacher.profilePicture?.cdn_url ? 'pi pi-user' : undefined}
              size="large" shape="circle"
              style={{ backgroundColor: '#E8EEF7', color: '#0e225d', border: '2px solid white', boxShadow: '0 2px 8px rgba(14,34,93,0.1)' }}
            />
            <div style={{ flex: 1 }}>
              <h3 style={{ margin: '0 0 6px 0', fontSize: '1rem', fontWeight: '700', color: '#0e225d' }}>
                {selectedTeacher.name}
              </h3>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', fontSize: '0.82rem', color: '#6b7280' }}>
                {selectedTeacher.employeeId && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <i className="pi pi-id-card" style={{ fontSize: '0.75rem', color: '#0e225d' }}></i>
                    {selectedTeacher.employeeId}
                  </span>
                )}
                {selectedTeacher.email && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <i className="pi pi-envelope" style={{ fontSize: '0.75rem', color: '#0e225d' }}></i>
                    {selectedTeacher.email}
                  </span>
                )}
                {selectedTeacher.mobileNumber && (
                  <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <i className="pi pi-phone" style={{ fontSize: '0.75rem', color: '#0e225d' }}></i>
                    {selectedTeacher.mobileNumber}
                  </span>
                )}
              </div>
            </div>
            {/* Status indicator */}
            <span style={{
              padding: '4px 12px', borderRadius: '20px',
              fontSize: '0.68rem', fontWeight: '700',
              background: 'rgba(22,163,74,0.1)', color: '#15803d',
              border: '1px solid rgba(22,163,74,0.25)'
            }}>
              Active
            </span>
          </div>
        )}
      </div>

      {/* ── Permissions Configuration ─────────────────────────────────────── */}
      {selectedTeacherId && (
        <>
          {permissionsLoading ? (
            <div style={{ ...cardStyle, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
              <div style={{ textAlign: 'center' }}>
                <ProgressSpinner style={{ width: '40px', height: '40px' }} strokeWidth="3" animationDuration=".8s" />
                <p style={{ marginTop: '12px', color: '#9ca3af', fontSize: '0.85rem' }}>Loading permissions...</p>
              </div>
            </div>
          ) : (
            <>
              {/* Module Permissions Card */}
              <div style={cardStyle}>
                <div style={cardHeaderStyle}>
                  <div style={{ width: '4px', height: '22px', background: 'linear-gradient(180deg, #fbb21f 0%, #e09a00 100%)', borderRadius: '4px' }} />
                  <div style={{
                    width: '38px', height: '38px',
                    background: 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)',
                    borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 2px 8px rgba(14,34,93,0.2)'
                  }}>
                    <i className="pi pi-shield" style={{ color: '#fbb21f', fontSize: '1rem' }}></i>
                  </div>
                  <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: '700', color: '#0e225d' }}>
                    Module Permissions
                  </h2>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                  {modules.map((module) => {
                    const mp = permissions[module.key];
                    const enabledCount = Object.values(mp.actions).filter(Boolean).length;

                    return (
                      <div
                        key={module.key}
                        style={{
                          border: `1px solid ${mp.enabled ? 'rgba(14,34,93,0.2)' : '#E5E7EB'}`,
                          borderRadius: '14px', padding: '20px',
                          background: mp.enabled ? 'rgba(14,34,93,0.02)' : 'white',
                          transition: 'all 0.25s ease',
                          boxShadow: mp.enabled ? '0 2px 10px rgba(14,34,93,0.07)' : 'none'
                        }}
                      >
                        {/* Module header row */}
                        <div style={{
                          display: 'flex', alignItems: 'center',
                          justifyContent: 'space-between',
                          marginBottom: '16px', paddingBottom: '14px',
                          borderBottom: `1px solid ${mp.enabled ? 'rgba(14,34,93,0.1)' : '#f3f4f6'}`
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            {/* Module icon */}
                            <div style={{
                              width: '38px', height: '38px', borderRadius: '10px',
                              background: mp.enabled
                                ? 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)'
                                : '#F5F7FA',
                              border: mp.enabled ? 'none' : '1px solid #E5E7EB',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              transition: 'all 0.25s ease',
                              boxShadow: mp.enabled ? '0 2px 8px rgba(14,34,93,0.2)' : 'none'
                            }}>
                              <i className={`pi ${module.icon}`}
                                style={{ color: mp.enabled ? '#fbb21f' : '#9ca3af', fontSize: '0.95rem' }}
                              ></i>
                            </div>
                            <div>
                              <h3 style={{ margin: '0 0 2px 0', fontSize: '0.92rem', fontWeight: '700', color: '#0e225d' }}>
                                {module.label}
                              </h3>
                              <p style={{ margin: 0, fontSize: '0.73rem', color: '#9ca3af' }}>
                                {module.description}
                              </p>
                            </div>
                          </div>

                          {/* Toggle */}
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            {mp.enabled && enabledCount > 0 && (
                              <span style={{
                                padding: '3px 10px', borderRadius: '20px',
                                fontSize: '0.65rem', fontWeight: '700',
                                background: 'rgba(251,178,31,0.15)',
                                color: '#b8860b', border: '1px solid rgba(251,178,31,0.3)'
                              }}>
                                {enabledCount}/4 actions
                              </span>
                            )}
                            <div
                              onClick={() => handleModuleToggle(module.key)}
                              style={{
                                display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer'
                              }}
                            >
                              <Checkbox
                                inputId={`module-${module.key}`}
                                checked={mp.enabled}
                                onChange={() => handleModuleToggle(module.key)}
                              />
                              <label
                                htmlFor={`module-${module.key}`}
                                style={{
                                  fontSize: '0.82rem', fontWeight: '700',
                                  color: mp.enabled ? '#15803d' : '#9ca3af',
                                  cursor: 'pointer', transition: 'color 0.2s ease'
                                }}
                              >
                                {mp.enabled ? 'Enabled' : 'Disabled'}
                              </label>
                            </div>
                          </div>
                        </div>

                        {/* Actions grid */}
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
                          gap: '10px',
                          opacity: mp.enabled ? 1 : 0.35,
                          pointerEvents: mp.enabled ? 'auto' : 'none',
                          transition: 'opacity 0.2s ease'
                        }}>
                          {(['create', 'read', 'update', 'delete'] as const).map((action) => {
                            const isOn = mp.actions[action];
                            const actionColors = {
                              create: { on: 'rgba(22,163,74,0.1)', border: 'rgba(22,163,74,0.3)', text: '#15803d' },
                              read: { on: 'rgba(14,34,93,0.08)', border: 'rgba(14,34,93,0.2)', text: '#0e225d' },
                              update: { on: 'rgba(251,178,31,0.12)', border: 'rgba(251,178,31,0.35)', text: '#b8860b' },
                              delete: { on: 'rgba(239,68,68,0.1)', border: 'rgba(239,68,68,0.3)', text: '#c62828' }
                            };
                            const col = actionColors[action];

                            return (
                              <div
                                key={action}
                                onClick={() => handleActionToggle(module.key, action)}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: '8px',
                                  padding: '10px 14px',
                                  background: isOn ? col.on : '#F5F7FA',
                                  border: `1px solid ${isOn ? col.border : '#E5E7EB'}`,
                                  borderRadius: '10px', cursor: 'pointer',
                                  transition: 'all 0.2s ease'
                                }}
                                onMouseEnter={(e) => {
                                  if (!isOn) {
                                    e.currentTarget.style.background = 'rgba(14,34,93,0.04)';
                                    e.currentTarget.style.borderColor = 'rgba(14,34,93,0.2)';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!isOn) {
                                    e.currentTarget.style.background = '#F5F7FA';
                                    e.currentTarget.style.borderColor = '#E5E7EB';
                                  }
                                }}
                              >
                                <Checkbox
                                  inputId={`${module.key}-${action}`}
                                  checked={isOn}
                                  onChange={() => handleActionToggle(module.key, action)}
                                  disabled={!mp.enabled}
                                />
                                <label
                                  htmlFor={`${module.key}-${action}`}
                                  style={{
                                    fontSize: '0.78rem', fontWeight: '700',
                                    color: isOn ? col.text : '#9ca3af',
                                    cursor: 'pointer', textTransform: 'capitalize',
                                    transition: 'color 0.2s ease'
                                  }}
                                >
                                  {action}
                                </label>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* ── Action buttons card ─────────────────────────────────── */}
              <div style={{ ...cardStyle, display: 'flex', gap: '12px', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', padding: '20px 24px' }}>
                {/* Save — navy gradient */}
                <button
                  onClick={handleSavePermissions}
                  disabled={saving || permissionsLoading}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '8px',
                    padding: '12px 28px',
                    background: saving || permissionsLoading
                      ? 'rgba(14,34,93,0.4)'
                      : 'linear-gradient(135deg, #0e225d 0%, #1a3370 100%)',
                    border: 'none', borderRadius: '12px', color: 'white',
                    fontSize: '0.88rem', fontWeight: '700',
                    cursor: saving || permissionsLoading ? 'not-allowed' : 'pointer',
                    boxShadow: '0 3px 10px rgba(14,34,93,0.25)',
                    transition: 'all 0.2s ease', letterSpacing: '0.3px'
                  }}
                  onMouseEnter={(e) => { if (!saving && !permissionsLoading) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 18px rgba(14,34,93,0.35)'; } }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 3px 10px rgba(14,34,93,0.25)'; }}
                >
                  <i className={`pi ${saving ? 'pi-spin pi-spinner' : 'pi-check'}`} style={{ fontSize: '0.88rem' }}></i>
                  {saving ? 'Saving...' : 'Save Permissions'}
                </button>

                {/* Reset — outlined */}
                <button
                  onClick={handleResetPermissions}
                  disabled={saving || permissionsLoading}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '8px',
                    padding: '12px 24px',
                    background: 'white', border: '1px solid #E5E7EB',
                    borderRadius: '12px', color: '#6b7280',
                    fontSize: '0.88rem', fontWeight: '700',
                    cursor: saving || permissionsLoading ? 'not-allowed' : 'pointer',
                    opacity: saving || permissionsLoading ? 0.5 : 1,
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => { if (!saving && !permissionsLoading) { e.currentTarget.style.borderColor = '#fbb21f'; e.currentTarget.style.color = '#0e225d'; e.currentTarget.style.background = '#F5F7FA'; } }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#E5E7EB'; e.currentTarget.style.color = '#6b7280'; e.currentTarget.style.background = 'white'; }}
                >
                  <i className="pi pi-refresh" style={{ fontSize: '0.88rem' }}></i>
                  Reset to Default
                </button>
              </div>
            </>
          )}
        </>
      )}

      {/* ── Empty state ───────────────────────────────────────────────────── */}
      {!selectedTeacherId && (
        <div style={{ ...cardStyle, textAlign: 'center', padding: '60px 24px' }}>
          <div style={{
            width: '72px', height: '72px', margin: '0 auto 20px',
            background: 'rgba(14,34,93,0.06)', borderRadius: '18px',
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <i className="pi pi-shield" style={{ fontSize: '1.8rem', color: '#0e225d', opacity: 0.3 }}></i>
          </div>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '1.1rem', fontWeight: '700', color: '#0e225d' }}>
            No Teacher Selected
          </h3>
          <p style={{ margin: 0, fontSize: '0.85rem', color: '#9ca3af' }}>
            Select a teacher from the dropdown above to manage their permissions
          </p>
        </div>
      )}
    </div>
  );
};

export default TeacherPermissionsSettings;

// import React, { useState, useEffect, useRef } from 'react';
// import { Toast } from 'primereact/toast';
// import { Message } from 'primereact/message';
// import { ProgressSpinner } from 'primereact/progressspinner';
// import { Avatar } from 'primereact/avatar';
// import { Checkbox } from 'primereact/checkbox';
// import { GlobalDropdownField, DropdownOption } from '../../components/GlobalComponents/GlobalDropdownField';
// import { GlobalButton } from '../../components/GlobalComponents/GlobalButton';
// import { useOperatorProfileStore } from '../../stateManagers/stores/operatorProfileStore';
// import apiClient from '../../stateManagers/apiSetup/axiosInterceptor';

// interface Teacher {
//   teacher_id: string;
//   name: string;
//   email: string;
//   mobileNumber: string;
//   employeeId: string;
//   qualification: string;
//   status: string;
//   profilePicture?: {
//     cdn_url?: string;
//     dev_url?: string;
//   };
// }

// interface TeacherPermissions {
//   noticeboard: {
//     enabled: boolean;
//     actions: {
//       create: boolean;
//       read: boolean;
//       update: boolean;
//       delete: boolean;
//     };
//   };
//   students: {
//     enabled: boolean;
//     actions: {
//       create: boolean;
//       read: boolean;
//       update: boolean;
//       delete: boolean;
//     };
//   };
//   fee_management: {
//     enabled: boolean;
//     actions: {
//       create: boolean;
//       read: boolean;
//       update: boolean;
//       delete: boolean;
//     };
//   };
// }

// interface TeacherPermissionSettings {
//   setting_id?: string;
//   teacher_id: string;
//   permissions: TeacherPermissions;
// }

// const TeacherPermissionsSettings: React.FC = () => {
//   const toast = useRef<Toast>(null);
//   const { profile, loading: profileLoading } = useOperatorProfileStore();

//   const [teachers, setTeachers] = useState<Teacher[]>([]);
//   const [teachersLoading, setTeachersLoading] = useState(false);
//   const [selectedTeacherId, setSelectedTeacherId] = useState<string | null>(null);
//   const [selectedTeacher, setSelectedTeacher] = useState<Teacher | null>(null);
//   const [permissionsLoading, setPermissionsLoading] = useState(false);
//   const [saving, setSaving] = useState(false);
//   const [error, setError] = useState<string | null>(null);

//   const [permissions, setPermissions] = useState<TeacherPermissions>({
//     noticeboard: {
//       enabled: false,
//       actions: { create: false, read: false, update: false, delete: false }
//     },
//     students: {
//       enabled: false,
//       actions: { create: false, read: false, update: false, delete: false }
//     },
//     fee_management: {
//       enabled: false,
//       actions: { create: false, read: false, update: false, delete: false }
//     }
//   });

//   // Toast helpers
//   const showSuccess = (detail: string, summary = 'Success') => {
//     toast.current?.show({
//       severity: 'success',
//       summary,
//       detail,
//       life: 3200,
//     });
//   };

//   const showError = (detail: string, summary = 'Error') => {
//     toast.current?.show({
//       severity: 'error',
//       summary,
//       detail,
//       life: 4500,
//     });
//   };

//   const showInfo = (detail: string, summary = 'Info') => {
//     toast.current?.show({
//       severity: 'info',
//       summary,
//       detail,
//       life: 3000,
//     });
//   };

//   // Fetch teachers
//   useEffect(() => {
//     if (profile?.institute_id && profile?.branch_id) {
//       fetchTeachers();
//     }
//   }, [profile]);

//   const fetchTeachers = async () => {
//     try {
//       setTeachersLoading(true);
//       setError(null);

//       const response = await apiClient.get('/v1/teachers/get-teachers', {
//         params: {
//           page: 1,
//           limit: 100,
//           status: 'active'
//         }
//       });

//       if (response.data.success) {
//         setTeachers(response.data.data);
//       } else {
//         showError('Failed to load teachers');
//       }
//     } catch (err: any) {
//       console.error('Failed to fetch teachers:', err);
//       showError(err.response?.data?.error || 'Failed to load teachers');
//     } finally {
//       setTeachersLoading(false);
//     }
//   };

//   // Fetch permissions when teacher is selected
//   useEffect(() => {
//     if (selectedTeacherId) {
//       fetchTeacherPermissions(selectedTeacherId);
//       const teacher = teachers.find(t => t.teacher_id === selectedTeacherId);
//       setSelectedTeacher(teacher || null);
//     } else {
//       setSelectedTeacher(null);
//       resetPermissions();
//     }
//   }, [selectedTeacherId, teachers]);

//   const fetchTeacherPermissions = async (teacherId: string) => {
//     try {
//       setPermissionsLoading(true);
//       setError(null);

//       const response = await apiClient.get(
//         `/v1/settings/teacher-permissions/${teacherId}`
//       );

//       if (response.data.success && response.data.data) {
//         setPermissions(response.data.data.permissions || getDefaultPermissions());
//       } else {
//         // No permissions found, use defaults
//         resetPermissions();
//       }
//     } catch (err: any) {
//       console.error('Failed to fetch permissions:', err);
//       // Don't show error for 404 (no permissions found), just use defaults
//       if (err.response?.status !== 404) {
//         showError(err.response?.data?.error || 'Failed to load permissions');
//       }
//       resetPermissions();
//     } finally {
//       setPermissionsLoading(false);
//     }
//   };

//   const getDefaultPermissions = (): TeacherPermissions => ({
//     noticeboard: {
//       enabled: false,
//       actions: { create: false, read: false, update: false, delete: false }
//     },
//     students: {
//       enabled: false,
//       actions: { create: false, read: false, update: false, delete: false }
//     },
//     fee_management: {
//       enabled: false,
//       actions: { create: false, read: false, update: false, delete: false }
//     }
//   });

//   const resetPermissions = () => {
//     setPermissions(getDefaultPermissions());
//   };

//   // Handle module enable/disable
//   const handleModuleToggle = (module: keyof TeacherPermissions) => {
//     setPermissions(prev => ({
//       ...prev,
//       [module]: {
//         ...prev[module],
//         enabled: !prev[module].enabled,
//         // If disabling, turn off all actions
//         actions: !prev[module].enabled 
//           ? prev[module].actions 
//           : { create: false, read: false, update: false, delete: false }
//       }
//     }));
//   };

//   // Handle action toggle
//   const handleActionToggle = (
//     module: keyof TeacherPermissions,
//     action: 'create' | 'read' | 'update' | 'delete'
//   ) => {
//     setPermissions(prev => ({
//       ...prev,
//       [module]: {
//         ...prev[module],
//         actions: {
//           ...prev[module].actions,
//           [action]: !prev[module].actions[action]
//         }
//       }
//     }));
//   };

//   // Save permissions
//   const handleSavePermissions = async () => {
//     if (!selectedTeacherId) {
//       showError('Please select a teacher first');
//       return;
//     }

//     if (!profile?.institute_id || !profile?.branch_id) {
//       showError('Institute or Branch information is missing');
//       return;
//     }

//     try {
//       setSaving(true);
//       setError(null);

//       const payload = {
//         institute_id: profile.institute_id,
//         branch_id: profile.branch_id,
//         teacher_id: selectedTeacherId,
//         permissions: permissions
//       };

//       const response = await apiClient.post(
//         '/v1/settings/teacher-permissions',
//         payload
//       );

//       if (response.data.success) {
//         showSuccess('Teacher permissions saved successfully!', 'Permissions Updated');
//       } else {
//         showError(response.data.message || 'Failed to save permissions');
//       }
//     } catch (err: any) {
//       console.error('Failed to save permissions:', err);
//       const errorMessage =
//         err.response?.data?.message ||
//         err.response?.data?.error ||
//         'Failed to save permissions';
//       showError(errorMessage);
//     } finally {
//       setSaving(false);
//     }
//   };

//   // Reset to defaults
//   const handleResetPermissions = () => {
//     if (selectedTeacherId) {
//       resetPermissions();
//       showInfo('Permissions reset to default (all disabled)');
//     }
//   };

//   // Teacher dropdown options
//   const teacherOptions: DropdownOption[] = teachers.map(teacher => ({
//     label: `${teacher.name} - ${teacher.employeeId || 'No ID'}`,
//     value: teacher.teacher_id
//   }));

//   // Module configuration
//   const modules = [
//     {
//       key: 'noticeboard' as keyof TeacherPermissions,
//       label: 'Noticeboard',
//       icon: 'pi-megaphone',
//       description: 'Manage notices and announcements',
//       color: '#3b82f6'
//     },
//     {
//       key: 'students' as keyof TeacherPermissions,
//       label: 'Students',
//       icon: 'pi-users',
//       description: 'View and manage student information',
//       color: '#10b981'
//     },
//     {
//       key: 'fee_management' as keyof TeacherPermissions,
//       label: 'Fee Management',
//       icon: 'pi-wallet',
//       description: 'Handle fee collection and records',
//       color: '#f59e0b'
//     }
//   ];

//   // Styles
//   const sectionStyle = {
//     background: 'white',
//     borderRadius: '16px',
//     padding: '24px',
//     marginBottom: '20px',
//     boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
//     border: '1px solid #f0f0f0'
//   };

//   const sectionHeaderStyle = {
//     display: 'flex',
//     alignItems: 'center',
//     gap: '12px',
//     marginBottom: '20px',
//     paddingBottom: '12px',
//     borderBottom: '2px solid #f0f0f0'
//   };

//   const iconBoxStyle = {
//     width: '40px',
//     height: '40px',
//     background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 100%)',
//     borderRadius: '10px',
//     display: 'flex',
//     alignItems: 'center',
//     justifyContent: 'center'
//   };

//   if (profileLoading) {
//     return (
//       <div style={{
//         display: 'flex',
//         justifyContent: 'center',
//         alignItems: 'center',
//         minHeight: '400px',
//         flexDirection: 'column',
//         gap: '16px'
//       }}>
//         <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="3" />
//         <p style={{ color: '#0f3d3e', fontSize: '0.95rem' }}>Loading...</p>
//       </div>
//     );
//   }

//   if (!profile) {
//     return (
//       <div style={{ padding: '24px' }}>
//         <Message
//           severity="warn"
//           text="Unable to load profile information. Please refresh the page."
//           style={{ fontSize: '0.9rem' }}
//         />
//       </div>
//     );
//   }

//   return (
//     <div style={{ padding: '0', margin: '0 auto' }}>
//       <Toast ref={toast} position="top-right" />

//       {/* Header */}
//       <div style={{
//         background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 50%, #0a2f2f 100%)',
//         borderRadius: '20px',
//         padding: '32px',
//         marginBottom: '24px',
//         color: 'white',
//         position: 'relative',
//         overflow: 'hidden',
//         boxShadow: '0 10px 40px rgba(15, 61, 62, 0.3)'
//       }}>
//         <div style={{
//           position: 'absolute',
//           top: '-50px',
//           right: '-50px',
//           width: '200px',
//           height: '200px',
//           background: 'rgba(212, 241, 126, 0.1)',
//           borderRadius: '50%',
//           filter: 'blur(40px)'
//         }} />
//         <div style={{ position: 'relative', zIndex: 1 }}>
//           <div style={{
//             display: 'inline-block',
//             padding: '5px 12px',
//             background: 'rgba(212, 241, 126, 0.2)',
//             borderRadius: '20px',
//             marginBottom: '10px',
//             fontSize: '0.7rem',
//             fontWeight: '600',
//             letterSpacing: '0.5px',
//             textTransform: 'uppercase'
//           }}>
//             Settings
//           </div>
//           <h1 style={{
//             margin: '0 0 8px 0',
//             fontSize: '1.7rem',
//             fontWeight: '700',
//             color: 'white',
//             letterSpacing: '-0.5px'
//           }}>
//             Teacher Permissions
//           </h1>
//           <p style={{
//             margin: '0',
//             color: 'rgba(255,255,255,0.8)',
//             fontSize: '0.9rem'
//           }}>
//             Manage access permissions for teachers
//           </p>
//         </div>
//       </div>

//       {error && (
//         <Message
//           severity="error"
//           text={error}
//           style={{ marginBottom: '20px', fontSize: '0.9rem' }}
//         />
//       )}

//       {/* Teacher Selection */}
//       <div style={sectionStyle}>
//         <div style={sectionHeaderStyle}>
//           <div style={iconBoxStyle}>
//             <i className="pi pi-user" style={{ color: '#d4f17e', fontSize: '1.1rem' }}></i>
//           </div>
//           <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: '#0f3d3e' }}>
//             Select Teacher
//           </h2>
//         </div>

//         <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: '16px', alignItems: 'end' }}>
//           <GlobalDropdownField
//             label="Teacher"
//             value={selectedTeacherId}
//             onChange={(val) => setSelectedTeacherId(val)}
//             options={teacherOptions}
//             placeholder="Select a teacher to manage permissions"
//             icon="pi pi-user"
//             filter
//           />
//           {selectedTeacherId && (
//             <GlobalButton
//               label="REFRESH"
//               icon="pi pi-refresh"
//               variant="outline"
//               onClick={() => fetchTeacherPermissions(selectedTeacherId)}
//               disabled={permissionsLoading}
//             />
//           )}
//         </div>

//         {/* Selected Teacher Info */}
//         {selectedTeacher && (
//           <div style={{
//             marginTop: '20px',
//             padding: '16px',
//             background: '#f9fafb',
//             borderRadius: '12px',
//             border: '1px solid #e5e7eb',
//             display: 'flex',
//             alignItems: 'center',
//             gap: '16px'
//           }}>
//             <Avatar
//               image={selectedTeacher.profilePicture?.cdn_url || undefined}
//               icon={!selectedTeacher.profilePicture?.cdn_url ? "pi pi-user" : undefined}
//               size="large"
//               shape="circle"
//               style={{
//                 backgroundColor: '#e5e7eb',
//                 border: '2px solid white',
//                 boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
//               }}
//             />
//             <div style={{ flex: 1 }}>
//               <h3 style={{
//                 margin: '0 0 4px 0',
//                 fontSize: '1rem',
//                 fontWeight: '700',
//                 color: '#0f3d3e'
//               }}>
//                 {selectedTeacher.name}
//               </h3>
//               <div style={{ display: 'flex', gap: '16px', fontSize: '0.85rem', color: '#666' }}>
//                 {selectedTeacher.employeeId && (
//                   <span>
//                     <i className="pi pi-id-card" style={{ marginRight: '6px' }}></i>
//                     {selectedTeacher.employeeId}
//                   </span>
//                 )}
//                 {selectedTeacher.email && (
//                   <span>
//                     <i className="pi pi-envelope" style={{ marginRight: '6px' }}></i>
//                     {selectedTeacher.email}
//                   </span>
//                 )}
//                 {selectedTeacher.mobileNumber && (
//                   <span>
//                     <i className="pi pi-phone" style={{ marginRight: '6px' }}></i>
//                     {selectedTeacher.mobileNumber}
//                   </span>
//                 )}
//               </div>
//             </div>
//           </div>
//         )}
//       </div>

//       {/* Permissions Configuration */}
//       {selectedTeacherId && (
//         <>
//           {permissionsLoading ? (
//             <div style={{
//               ...sectionStyle,
//               display: 'flex',
//               justifyContent: 'center',
//               alignItems: 'center',
//               minHeight: '200px'
//             }}>
//               <div style={{ textAlign: 'center' }}>
//                 <ProgressSpinner style={{ width: '40px', height: '40px' }} strokeWidth="3" />
//                 <p style={{ marginTop: '12px', color: '#666', fontSize: '0.85rem' }}>
//                   Loading permissions...
//                 </p>
//               </div>
//             </div>
//           ) : (
//             <>
//               <div style={sectionStyle}>
//                 <div style={sectionHeaderStyle}>
//                   <div style={iconBoxStyle}>
//                     <i className="pi pi-shield" style={{ color: '#d4f17e', fontSize: '1.1rem' }}></i>
//                   </div>
//                   <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '700', color: '#0f3d3e' }}>
//                     Module Permissions
//                   </h2>
//                 </div>

//                 <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
//                   {modules.map((module) => {
//                     const modulePermission = permissions[module.key];
//                     return (
//                       <div
//                         key={module.key}
//                         style={{
//                           border: '1px solid #e5e7eb',
//                           borderRadius: '12px',
//                           padding: '20px',
//                           background: modulePermission.enabled ? '#f0fdf4' : 'white',
//                           transition: 'all 0.2s ease'
//                         }}
//                       >
//                         {/* Module Header */}
//                         <div style={{
//                           display: 'flex',
//                           alignItems: 'center',
//                           justifyContent: 'space-between',
//                           marginBottom: '16px',
//                           paddingBottom: '16px',
//                           borderBottom: '1px solid #e5e7eb'
//                         }}>
//                           <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
//                             <div style={{
//                               width: '36px',
//                               height: '36px',
//                               borderRadius: '8px',
//                               background: modulePermission.enabled ? module.color : '#e5e7eb',
//                               display: 'flex',
//                               alignItems: 'center',
//                               justifyContent: 'center',
//                               transition: 'all 0.2s ease'
//                             }}>
//                               <i
//                                 className={`pi ${module.icon}`}
//                                 style={{ color: 'white', fontSize: '1rem' }}
//                               ></i>
//                             </div>
//                             <div>
//                               <h3 style={{
//                                 margin: '0 0 2px 0',
//                                 fontSize: '0.95rem',
//                                 fontWeight: '700',
//                                 color: '#0f3d3e'
//                               }}>
//                                 {module.label}
//                               </h3>
//                               <p style={{
//                                 margin: 0,
//                                 fontSize: '0.75rem',
//                                 color: '#666'
//                               }}>
//                                 {module.description}
//                               </p>
//                             </div>
//                           </div>
//                           <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
//                             <Checkbox
//                               inputId={`module-${module.key}`}
//                               checked={modulePermission.enabled}
//                               onChange={() => handleModuleToggle(module.key)}
//                             />
//                             <label
//                               htmlFor={`module-${module.key}`}
//                               style={{
//                                 fontSize: '0.85rem',
//                                 fontWeight: '600',
//                                 color: modulePermission.enabled ? '#16a34a' : '#6b7280',
//                                 cursor: 'pointer'
//                               }}
//                             >
//                               {modulePermission.enabled ? 'Enabled' : 'Disabled'}
//                             </label>
//                           </div>
//                         </div>

//                         {/* Actions Grid */}
//                         <div style={{
//                           display: 'grid',
//                           gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
//                           gap: '12px',
//                           opacity: modulePermission.enabled ? 1 : 0.4,
//                           pointerEvents: modulePermission.enabled ? 'auto' : 'none'
//                         }}>
//                           {(['create', 'read', 'update', 'delete'] as const).map((action) => (
//                             <div
//                               key={action}
//                               style={{
//                                 display: 'flex',
//                                 alignItems: 'center',
//                                 gap: '8px',
//                                 padding: '10px 12px',
//                                 background: modulePermission.actions[action] ? '#dcfce7' : 'white',
//                                 border: '1px solid #e5e7eb',
//                                 borderRadius: '8px',
//                                 cursor: 'pointer',
//                                 transition: 'all 0.2s ease'
//                               }}
//                               onClick={() => handleActionToggle(module.key, action)}
//                             >
//                               <Checkbox
//                                 inputId={`${module.key}-${action}`}
//                                 checked={modulePermission.actions[action]}
//                                 onChange={() => handleActionToggle(module.key, action)}
//                                 disabled={!modulePermission.enabled}
//                               />
//                               <label
//                                 htmlFor={`${module.key}-${action}`}
//                                 style={{
//                                   fontSize: '0.8rem',
//                                   fontWeight: '600',
//                                   color: modulePermission.actions[action] ? '#16a34a' : '#6b7280',
//                                   cursor: 'pointer',
//                                   textTransform: 'capitalize'
//                                 }}
//                               >
//                                 {action}
//                               </label>
//                             </div>
//                           ))}
//                         </div>
//                       </div>
//                     );
//                   })}
//                 </div>
//               </div>

//               {/* Action Buttons */}
//               <div style={{
//                 ...sectionStyle,
//                 display: 'flex',
//                 gap: '12px',
//                 justifyContent: 'center',
//                 alignItems: 'center',
//                 flexWrap: 'wrap'
//               }}>
//                 <GlobalButton
//                   label={saving ? 'Saving...' : 'Save Permissions'}
//                   variant="primary"
//                   icon={saving ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
//                   onClick={handleSavePermissions}
//                   disabled={saving || permissionsLoading}
//                   loading={saving}
//                   size="large"
//                 />
//                 <GlobalButton
//                   label="Reset to Default"
//                   variant="outline"
//                   icon="pi pi-refresh"
//                   onClick={handleResetPermissions}
//                   disabled={saving || permissionsLoading}
//                   size="large"
//                 />
//               </div>
//             </>
//           )}
//         </>
//       )}

//       {/* Empty State */}
//       {!selectedTeacherId && (
//         <div style={{
//           ...sectionStyle,
//           textAlign: 'center',
//           padding: '60px 24px'
//         }}>
//           <div style={{
//             width: '80px',
//             height: '80px',
//             margin: '0 auto 20px',
//             background: '#f3f4f6',
//             borderRadius: '50%',
//             display: 'flex',
//             alignItems: 'center',
//             justifyContent: 'center'
//           }}>
//             <i className="pi pi-shield" style={{ fontSize: '2rem', color: '#9ca3af' }}></i>
//           </div>
//           <h3 style={{
//             margin: '0 0 8px 0',
//             fontSize: '1.1rem',
//             fontWeight: '700',
//             color: '#0f3d3e'
//           }}>
//             No Teacher Selected
//           </h3>
//           <p style={{
//             margin: 0,
//             fontSize: '0.85rem',
//             color: '#6b7280'
//           }}>
//             Select a teacher from the dropdown above to manage their permissions
//           </p>
//         </div>
//       )}
//     </div>
//   );
// };

// export default TeacherPermissionsSettings;
